import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Package } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Field, Input, Textarea, FormActions } from '../components/FormField';
import { useAuth } from '../../context/AuthContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm space-y-4">
      <h2 className="font-semibold text-gray-700 text-base border-b border-gray-100 pb-2">{title}</h2>
      {children}
    </div>
  );
}

export default function AdminHomePage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canAddProduct = can('products', 'create');
  const [heroDoc, setHeroDoc] = useState(null);
  const [heroForm, setHeroForm] = useState({ title: '', subtitle: '', body: '', btn1Label: 'Explore Products', btn1Link: '/products', btn2Label: 'Our Solutions', btn2Link: '/solutions' });
  const [heroFiles, setHeroFiles] = useState([]);
  const [heroLoading, setHeroLoading] = useState(false);

  const [statsForm, setStatsForm] = useState([
    { icon: 'Star', value: '500+', label: 'Premium Products' },
    { icon: 'Award', value: '20+', label: 'Years Experience' },
    { icon: 'Globe', value: '50+', label: 'Countries Served' },
  ]);
  const [statsLoading, setStatsLoading] = useState(false);

  const [catsForm, setCatsForm] = useState([
    { id: 'sample-catalogue', label: 'Sample Catalogue', img: '' },
    { id: 'sample-books', label: 'Sample Books', img: '' },
    { id: 'hangers-waterfalls', label: 'Hangers & Waterfalls', img: '' },
    { id: 'swatches', label: 'Swatches', img: '' },
  ]);
  const [catsFiles, setCatsFiles] = useState([null, null, null, null]);
  const [catsPreviews, setCatsPreviews] = useState([null, null, null, null]);
  const [catsLoading, setCatsLoading] = useState(false);

  const [ctaForm, setCtaForm] = useState({ title: '', body: '', btnLabel: 'Our Solutions', btnLink: '/solutions', imageUrl: '' });
  const [ctaFile, setCtaFile] = useState(null);
  const [ctaPreview, setCtaPreview] = useState(null);
  const [ctaLoading, setCtaLoading] = useState(false);

  const [solForm, setSolForm] = useState({ title: 'Sample Solutions', body: '', btnLabel: 'Learn More', btnLink: '/solutions' });
  const [solLoading, setSolLoading] = useState(false);

  useEffect(() => {
    api.get('/content?page=home').then(res => {
      const items = res.data.content || [];
      const find = s => items.find(c => c.section === s);

      const h = find('hero');
      if (h) {
        setHeroDoc(h);
        setHeroForm({
          title: h.title || '',
          subtitle: h.subtitle || '',
          body: h.body || '',
          btn1Label: h.ctaButtons?.[0]?.label || 'Explore Products',
          btn1Link: h.ctaButtons?.[0]?.link || '/products',
          btn2Label: h.ctaButtons?.[1]?.label || 'Our Solutions',
          btn2Link: h.ctaButtons?.[1]?.link || '/solutions',
        });
      }

      const st = find('stats');
      if (st?.extraData?.items?.length) setStatsForm(st.extraData.items);

      const cats = find('categories');
      if (cats?.extraData?.items?.length) {
        setCatsForm(cats.extraData.items);
        setCatsPreviews(cats.extraData.items.map(c => c.img || c.image || null));
      }

      const cta = find('cta-banner');
      if (cta) {
        setCtaForm({ title: cta.title || '', body: cta.body || '', btnLabel: cta.ctaButtons?.[0]?.label || 'Our Solutions', btnLink: cta.ctaButtons?.[0]?.link || '/solutions', imageUrl: cta.image || cta.images?.[0] || '' });
        setCtaPreview(cta.image || cta.images?.[0] || null);
      }

      const sol = find('solutions-preview');
      if (sol) setSolForm({ title: sol.title || '', body: sol.body || '', btnLabel: sol.ctaButtons?.[0]?.label || 'Learn More', btnLink: sol.ctaButtons?.[0]?.link || '/solutions' });
    }).catch(() => {});
  }, []);

  const saveHeroText = async (e) => {
    e.preventDefault();
    setHeroLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'home'); fd.append('section', 'hero');
      fd.append('title', heroForm.title);
      fd.append('subtitle', heroForm.subtitle);
      fd.append('body', heroForm.body);
      fd.append('ctaButtons', JSON.stringify([
        { label: heroForm.btn1Label, link: heroForm.btn1Link },
        { label: heroForm.btn2Label, link: heroForm.btn2Link },
      ]));
      const res = await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroDoc(res.data.content);
      toast.success('Hero text saved');
    } catch { toast.error('Error saving hero'); }
    finally { setHeroLoading(false); }
  };

  const uploadHeroImages = async () => {
    if (!heroFiles.length) return;
    setHeroLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'home'); fd.append('section', 'hero');
      heroFiles.forEach(f => fd.append('images', f));
      const res = await api.post('/content/hero-images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroDoc(res.data.content);
      setHeroFiles([]);
      toast.success('Images uploaded');
    } catch { toast.error('Error uploading images'); }
    finally { setHeroLoading(false); }
  };

  const removeHeroImage = async (idx) => {
    if (!heroDoc?._id) return;
    try {
      const res = await api.delete(`/content/${heroDoc._id}/images/${idx}`);
      setHeroDoc(res.data.content);
      toast.success('Image removed');
    } catch { toast.error('Error removing image'); }
  };

  const saveStats = async (e) => {
    e.preventDefault();
    setStatsLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'home'); fd.append('section', 'stats');
      fd.append('extraData', JSON.stringify({ items: statsForm }));
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Stats saved');
    } catch { toast.error('Error saving stats'); }
    finally { setStatsLoading(false); }
  };

  const saveCategories = async (e) => {
    e.preventDefault();
    setCatsLoading(true);
    try {
      // Upload any selected files first, get back plain URLs
      const updatedCats = await Promise.all(catsForm.map(async (cat, i) => {
        if (catsFiles[i]) {
          const fd = new FormData();
          fd.append('images', catsFiles[i]);
          const res = await api.post('/content/upload-images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const uploadedUrl = res.data.urls?.[0] || cat.img;
          return { ...cat, img: uploadedUrl };
        }
        return cat;
      }));

      const fd = new FormData();
      fd.append('page', 'home');
      fd.append('section', 'categories');
      fd.append('extraData', JSON.stringify({ items: updatedCats }));
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCatsForm(updatedCats);
      setCatsFiles([null, null, null, null]);
      toast.success('Categories saved');
    } catch { toast.error('Error saving categories'); }
    finally { setCatsLoading(false); }
  };

  const saveCta = async (e) => {
    e.preventDefault();
    setCtaLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'home'); fd.append('section', 'cta-banner');
      fd.append('title', ctaForm.title);
      fd.append('body', ctaForm.body);
      fd.append('ctaButtons', JSON.stringify([{ label: ctaForm.btnLabel, link: ctaForm.btnLink }]));
      // If URL provided directly, store as image field
      if (ctaForm.imageUrl && !ctaFile) fd.append('image', ctaForm.imageUrl);
      // If file selected, upload it (stored as image field via 'image' multer field)
      if (ctaFile) fd.append('image', ctaFile);
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('CTA banner saved');
    } catch { toast.error('Error saving CTA'); }
    finally { setCtaLoading(false); }
  };

  const saveSolutions = async (e) => {
    e.preventDefault();
    setSolLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'home'); fd.append('section', 'solutions-preview');
      fd.append('title', solForm.title);
      fd.append('body', solForm.body);
      fd.append('ctaButtons', JSON.stringify([{ label: solForm.btnLabel, link: solForm.btnLink }]));
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Solutions preview saved');
    } catch { toast.error('Error saving solutions preview'); }
    finally { setSolLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-gray-800">Home Page Editor</h1>
          <p className="text-gray-500 text-sm">Edit all sections of the home page. Changes are live immediately.</p>
        </div>
        {canAddProduct && (
          <button
            onClick={() => navigate('/admin/products')}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
            <Package size={14} /> Add Product
          </button>
        )}
      </div>

      {/* Hero Text */}
      <SectionCard title="Hero Section — Text & Buttons">
        <form onSubmit={saveHeroText} className="space-y-4">
          <Field label="Main Title" hint="Large heading shown on the hero (e.g. 'The Sample House')">
            <Input value={heroForm.title} onChange={e => setHeroForm({ ...heroForm, title: e.target.value })} placeholder="The Sample House" />
          </Field>
          <Field label="Subtitle / Tagline" hint="Small text above the title (e.g. 'Premium Sample Solutions')">
            <Input value={heroForm.subtitle} onChange={e => setHeroForm({ ...heroForm, subtitle: e.target.value })} placeholder="Premium Sample Solutions" />
          </Field>
          <Field label="Description" hint="Short paragraph below the title">
            <Textarea value={heroForm.body} onChange={e => setHeroForm({ ...heroForm, body: e.target.value })} placeholder="Crafting excellence in every sample..." rows={3} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Button 1 Label"><Input value={heroForm.btn1Label} onChange={e => setHeroForm({ ...heroForm, btn1Label: e.target.value })} /></Field>
            <Field label="Button 1 Link"><Input value={heroForm.btn1Link} onChange={e => setHeroForm({ ...heroForm, btn1Link: e.target.value })} /></Field>
            <Field label="Button 2 Label"><Input value={heroForm.btn2Label} onChange={e => setHeroForm({ ...heroForm, btn2Label: e.target.value })} /></Field>
            <Field label="Button 2 Link"><Input value={heroForm.btn2Link} onChange={e => setHeroForm({ ...heroForm, btn2Link: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={heroLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {heroLoading ? 'Saving...' : 'Save Text'}
            </button>
          </div>
        </form>
      </SectionCard>

      {/* Hero Images */}
      <SectionCard title="Hero Section — Background Images">
        <p className="text-xs text-gray-500">Upload one or more images. Multiple images create a slideshow.</p>
        {heroDoc?.images?.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {heroDoc.images.map((url, i) => (
              <div key={i} className="relative group">
                <img src={url.startsWith('http') ? url : `${BASE}${url}`} alt={`Hero ${i+1}`} className="w-24 h-16 object-cover rounded border border-gray-200" />
                <button onClick={() => removeHeroImage(i)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3">
          <input type="file" multiple accept="image/*" onChange={e => setHeroFiles([...e.target.files])} className="text-sm text-gray-600 flex-1" />
          <button onClick={uploadHeroImages} disabled={!heroFiles.length || heroLoading}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50">
            <Plus size={14} /> Upload
          </button>
        </div>
      </SectionCard>

      {/* Stats */}
      <SectionCard title="Stats Section">
        <form onSubmit={saveStats} className="space-y-3">
          {statsForm.map((s, i) => (
            <div key={i} className="grid grid-cols-3 gap-3">
              <Field label={`Stat ${i+1} Value`}><Input value={s.value} onChange={e => { const n=[...statsForm]; n[i]={...n[i],value:e.target.value}; setStatsForm(n); }} placeholder="500+" /></Field>
              <Field label="Label"><Input value={s.label} onChange={e => { const n=[...statsForm]; n[i]={...n[i],label:e.target.value}; setStatsForm(n); }} placeholder="Premium Products" /></Field>
              <Field label="Icon" hint="Star, Award, or Globe"><Input value={s.icon} onChange={e => { const n=[...statsForm]; n[i]={...n[i],icon:e.target.value}; setStatsForm(n); }} placeholder="Star" /></Field>
            </div>
          ))}
          <div className="flex justify-end">
            <button type="submit" disabled={statsLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {statsLoading ? 'Saving...' : 'Save Stats'}
            </button>
          </div>
        </form>
      </SectionCard>

      {/* Categories */}
      <SectionCard title="Product Categories Section">
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
          Upload an image file or paste an Unsplash/web URL for each category.
        </div>
        <form onSubmit={saveCategories} className="space-y-4">
          {catsForm.map((c, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label={`Category ${i+1} Label`}>
                  <Input value={c.label} onChange={e => { const n=[...catsForm]; n[i]={...n[i],label:e.target.value}; setCatsForm(n); }} />
                </Field>
                <Field label="ID / Slug">
                  <Input value={c.id} onChange={e => { const n=[...catsForm]; n[i]={...n[i],id:e.target.value}; setCatsForm(n); }} />
                </Field>
              </div>
              <div className="flex gap-3 items-start">
                {/* Preview */}
                <div className="w-20 h-14 border border-gray-200 rounded overflow-hidden bg-gray-50 shrink-0">
                  {(catsPreviews[i] || c.img || c.image) ? (
                    <img src={catsPreviews[i] || c.img || c.image} alt={c.label}
                      className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No image</div>
                  )}
                </div>
                <div className="flex-1 space-y-1">
                  <Field label="Upload Image" hint="Select a file from your computer">
                    <input type="file" accept="image/*"
                      onChange={e => {
                        const file = e.target.files[0];
                        const newFiles = [...catsFiles]; newFiles[i] = file; setCatsFiles(newFiles);
                        const newPreviews = [...catsPreviews]; newPreviews[i] = file ? URL.createObjectURL(file) : catsPreviews[i]; setCatsPreviews(newPreviews);
                      }}
                      className="text-sm text-gray-600 w-full" />
                  </Field>
                  <Field label="Or paste image URL" hint="Unsplash or any web image URL">
                    <Input value={c.img||c.image||''} placeholder="https://images.unsplash.com/..."
                      onChange={e => {
                        const n=[...catsForm]; n[i]={...n[i],img:e.target.value}; setCatsForm(n);
                        const newPreviews=[...catsPreviews]; newPreviews[i]=e.target.value||null; setCatsPreviews(newPreviews);
                      }} />
                  </Field>
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <button type="submit" disabled={catsLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {catsLoading ? 'Saving...' : 'Save Categories'}
            </button>
          </div>
        </form>
      </SectionCard>

      {/* CTA Banner */}
      <SectionCard title="CTA Banner Section">
        <form onSubmit={saveCta} className="space-y-4">
          <Field label="Heading"><Input value={ctaForm.title} onChange={e => setCtaForm({...ctaForm,title:e.target.value})} placeholder="Explore Our Sample Collection" /></Field>
          <Field label="Description"><Textarea value={ctaForm.body} onChange={e => setCtaForm({...ctaForm,body:e.target.value})} rows={2} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Button Label"><Input value={ctaForm.btnLabel} onChange={e => setCtaForm({...ctaForm,btnLabel:e.target.value})} /></Field>
            <Field label="Button Link"><Input value={ctaForm.btnLink} onChange={e => setCtaForm({...ctaForm,btnLink:e.target.value})} /></Field>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-24 h-16 border border-gray-200 rounded overflow-hidden bg-gray-50 shrink-0">
              {(ctaPreview || ctaForm.imageUrl) ? (
                <img src={ctaPreview || ctaForm.imageUrl} alt="CTA preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No image</div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <Field label="Upload Background Image" hint="Select a file from your computer">
                <input type="file" accept="image/*"
                  onChange={e => {
                    const f = e.target.files[0];
                    setCtaFile(f);
                    setCtaPreview(f ? URL.createObjectURL(f) : ctaPreview);
                  }}
                  className="text-sm text-gray-600 w-full" />
              </Field>
              <Field label="Or paste image URL" hint="Unsplash or any web image URL">
                <Input value={ctaForm.imageUrl} placeholder="https://images.unsplash.com/..."
                  onChange={e => {
                    setCtaForm({...ctaForm, imageUrl: e.target.value});
                    setCtaPreview(e.target.value || null);
                    setCtaFile(null);
                  }} />
              </Field>
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={ctaLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {ctaLoading ? 'Saving...' : 'Save CTA Banner'}
            </button>
          </div>
        </form>
      </SectionCard>

      {/* Solutions Preview */}
      <SectionCard title="Solutions Preview Section">
        <form onSubmit={saveSolutions} className="space-y-4">
          <Field label="Heading"><Input value={solForm.title} onChange={e => setSolForm({...solForm,title:e.target.value})} placeholder="Sample Solutions" /></Field>
          <Field label="Description"><Textarea value={solForm.body} onChange={e => setSolForm({...solForm,body:e.target.value})} rows={2} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Button Label"><Input value={solForm.btnLabel} onChange={e => setSolForm({...solForm,btnLabel:e.target.value})} /></Field>
            <Field label="Button Link"><Input value={solForm.btnLink} onChange={e => setSolForm({...solForm,btnLink:e.target.value})} /></Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={solLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {solLoading ? 'Saving...' : 'Save Solutions Preview'}
            </button>
          </div>
        </form>
      </SectionCard>
    </div>
  );
}
